/**
 * ── Commercial Catalog ──
 * Central definition of MemberOS packaging: plans, modules, features, add-ons, bundles.
 * This is the single source of truth for what can be sold, bundled, gated, or upsold.
 *
 * NOTE: This is a client-side catalog for UI rendering and access resolution.
 * The authoritative entitlement state lives in the module/capability system
 * (workspace_modules, workspace_capabilities, and their enabled_* tables).
 */

// ─── Plans ───────────────────────────────────────────────────────────────────

export type PlanId = "core" | "professional" | "premium" | "enterprise";

export interface PlanDefinition {
  id: PlanId;
  name: string;
  tagline: string;
  priceRange: string;
  sortOrder: number;
  includedModules: string[];
  includedCapabilities: string[];
  /** Association-specific packaging metadata (enterprise plan only) */
  associationPackaging?: {
    includedOrgBaseline: PlanId;
    associationCapabilityKeys: string[];
    allowChildOrgUpgrades: boolean;
  };
}

export const PLANS: Record<PlanId, PlanDefinition> = {
  core: {
    id: "core",
    name: "Core",
    tagline: "Essential membership management",
    priceRange: "$297/mo",
    sortOrder: 1,
    includedModules: ["membership", "events", "communications", "crm"],
    includedCapabilities: [
      "core.workspace",
      "core.permissions",
      "membership.directory",
      "membership.tiers",
      "events.create",
      "events.registrations",
      "communications.campaigns",
      "communications.templates",
    ],
  },
  professional: {
    id: "professional",
    name: "Professional",
    tagline: "Growth and engagement tools",
    priceRange: "$797/mo",
    sortOrder: 2,
    includedModules: [
      "membership",
      "events",
      "communications",
      "crm",
      "sponsors",
      "programs",
      "community",
      "billing",
      "forms",
    ],
    includedCapabilities: [
      "core.workspace",
      "core.permissions",
      "membership.directory",
      "membership.tiers",
      "membership.retention",
      "events.create",
      "events.registrations",
      "events.surveys",
      "events.reporting",
      "communications.campaigns",
      "communications.templates",
      "communications.journeys",
      "sponsors.packages",
      "sponsors.fulfillment",
      "programs.cohorts",
      "programs.multi_session",
      "community.intros",
      "community.graph",
      "billing.invoicing",
      "billing.subscriptions",
      "forms.builder",
      "forms.analytics",
    ],
  },
  premium: {
    id: "premium",
    name: "Premium",
    tagline: "Full operational platform",
    priceRange: "$1,997/mo",
    sortOrder: 3,
    includedModules: [
      "membership",
      "events",
      "communications",
      "crm",
      "sponsors",
      "programs",
      "community",
      "billing",
      "forms",
      "funnels",
      "website_builder",
      "signature_events",
      "executive_intelligence",
      "learning",
      "playbooks",
    ],
    includedCapabilities: [
      "core.workspace",
      "core.permissions",
      "membership.directory",
      "membership.tiers",
      "membership.retention",
      "events.create",
      "events.registrations",
      "events.surveys",
      "events.reporting",
      "communications.campaigns",
      "communications.templates",
      "communications.journeys",
      "sponsors.packages",
      "sponsors.fulfillment",
      "sponsors.reporting",
      "programs.cohorts",
      "programs.multi_session",
      "programs.reporting",
      "community.intros",
      "community.graph",
      "community.pipeline",
      "billing.invoicing",
      "billing.subscriptions",
      "forms.builder",
      "forms.analytics",
      "forms.logic",
      "funnels.builder",
      "funnels.templates",
      "funnels.analytics",
      "website.builder",
      "website.publishing",
      "signature_events.budgeting",
      "signature_events.milestones",
      "executive.strategic",
      "executive.board_reports",
      "learning.courses",
      "learning.certifications",
      "playbooks.library",
      "playbooks.launch",
    ],
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Multi-org intelligence platform",
    priceRange: "$4,997/mo",
    sortOrder: 4,
    includedModules: [
      "membership",
      "events",
      "communications",
      "crm",
      "sponsors",
      "programs",
      "community",
      "billing",
      "forms",
      "funnels",
      "website_builder",
      "signature_events",
      "executive_intelligence",
      "learning",
      "playbooks",
      "association_management",
      "api_access",
    ],
    includedCapabilities: [
      // All Premium capabilities plus:
      "cross_org.benchmarking",
      "executive.growth_gaps",
      "api.keys",
      "api.webhooks",
      "association.governance",
      "association.hierarchy",
      "association.distribution",
      "association.centralized_billing",
      "association.network_exchange",
    ],
    /** Association-specific composition metadata */
    associationPackaging: {
      includedOrgBaseline: "premium" as PlanId,
      associationCapabilityKeys: [
        "association.governance",
        "association.hierarchy",
        "association.distribution",
        "association.centralized_billing",
        "association.network_exchange",
        "cross_org.benchmarking",
        "api.keys",
        "api.webhooks",
      ],
      allowChildOrgUpgrades: false,
    },
  },
};

export const PLAN_LIST = Object.values(PLANS).sort((a, b) => a.sortOrder - b.sortOrder);

// ─── Add-Ons ─────────────────────────────────────────────────────────────────

export interface AddOnDefinition {
  id: string;
  name: string;
  /** Internal/admin description */
  description: string;
  /** Marketing-facing description (for pricing pages) — falls back to description */
  marketingDescription?: string;
  price: string;
  /** Module keys this add-on unlocks */
  modules: string[];
  /** Capability keys this add-on unlocks */
  capabilities: string[];
  /** Minimum plan required to purchase (internal PlanId) */
  minPlan: PlanId;
  /** Public-facing minimum plan label (e.g. "Core", "Growth") */
  minPlanLabel: string;
  category: "module" | "feature" | "bundle";
  /** Lucide icon key for rendering */
  iconKey: string;
  /** Who can purchase: org, association, or both */
  audienceType: "org" | "association" | "both";
  /** Show on public pricing page */
  visibleOnPricing: boolean;
  /** Show in admin packaging UI */
  visibleInAdmin: boolean;
  /** Sort order for display */
  sortOrder: number;
  /** Whether this add-on is currently active/saleable */
  isActive: boolean;
}

export const ADD_ONS: AddOnDefinition[] = [
  {
    id: "addon_signature_events",
    name: "Signature Events",
    description: "Advanced event management with budgeting, milestones, and speaker management",
    marketingDescription:
      "Flagship event workspaces with budgets, milestones, speaker coordination, and dedicated dashboards.",
    price: "$199/mo",
    modules: ["signature_events"],
    capabilities: ["signature_events.budgeting", "signature_events.milestones", "signature_events.speakers"],
    minPlan: "professional",
    minPlanLabel: "Growth",
    category: "module",
    iconKey: "Zap",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 1,
    isActive: true,
  },
  {
    id: "addon_playbooks",
    name: "Operational Playbooks",
    description: "Operational playbooks for repeatable workflows and launches",
    marketingDescription:
      "Repeatable workflow templates for team coordination — from event planning to renewal campaigns.",
    price: "$99/mo",
    modules: ["playbooks"],
    capabilities: ["playbooks.library", "playbooks.launch"],
    minPlan: "professional",
    minPlanLabel: "Growth",
    category: "module",
    iconKey: "BookOpen",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 2,
    isActive: true,
  },
  {
    id: "addon_funnels",
    name: "Campaign Funnels",
    description: "Build conversion funnels and landing pages for recruitment and events",
    marketingDescription:
      "Goal-driven conversion funnels with 12 launch templates, A/B testing, exit-intent, and Stripe checkout.",
    price: "$149/mo",
    modules: ["funnels"],
    capabilities: ["funnels.builder", "funnels.templates", "funnels.analytics"],
    minPlan: "core",
    minPlanLabel: "Core",
    category: "module",
    iconKey: "Layers",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 3,
    isActive: true,
  },
  {
    id: "addon_website",
    name: "Website Builder",
    description: "Build and publish your organization's public website",
    marketingDescription:
      "Canvas-first WYSIWYG website builder with branded domains, Page Blueprints, and AI Strategy Wizard.",
    price: "$149/mo",
    modules: ["website_builder"],
    capabilities: ["website.builder", "website.publishing"],
    minPlan: "core",
    minPlanLabel: "Core",
    category: "module",
    iconKey: "Globe",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 4,
    isActive: true,
  },
  {
    id: "addon_learning",
    name: "Learning & Certifications",
    description: "Course builder, learning paths, and member certifications",
    marketingDescription:
      "Learning paths, course management, certifications with expiration tracking, and member progress reporting.",
    price: "$99/mo",
    modules: ["learning"],
    capabilities: ["learning.courses", "learning.certifications"],
    minPlan: "core",
    minPlanLabel: "Core",
    category: "module",
    iconKey: "GraduationCap",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 5,
    isActive: true,
  },
  {
    id: "addon_intelligence",
    name: "Executive Intelligence",
    description: "Board reporting, renewal risk analysis, and strategic insights",
    marketingDescription:
      "Board reports with AI narratives, renewal risk analysis, growth gap detection, and shareable reports.",
    price: "$299/mo",
    modules: ["executive_intelligence"],
    capabilities: ["executive.strategic", "executive.board_reports", "executive.growth_gaps"],
    minPlan: "professional",
    minPlanLabel: "Growth",
    category: "module",
    iconKey: "BarChart3",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 6,
    isActive: true,
  },
  {
    id: "addon_sponsor_roi",
    name: "Sponsor ROI Suite",
    description: "Advanced sponsor fulfillment tracking and ROI reports",
    marketingDescription:
      "Advanced sponsorship fulfillment tracking, credit-based booking engine, and sponsor portal with ROI dashboards.",
    price: "$99/mo",
    modules: [],
    capabilities: ["sponsors.reporting", "sponsors.fulfillment"],
    minPlan: "professional",
    minPlanLabel: "Growth",
    category: "feature",
    iconKey: "PieChart",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 7,
    isActive: true,
  },
  {
    id: "addon_advanced_forms",
    name: "Advanced Forms",
    description: "Advanced form builder with logic branching and analytics",
    marketingDescription:
      "40+ field types, logic branching, NPS surveys, conversational layouts, and registration form builder.",
    price: "$99/mo",
    modules: ["forms"],
    capabilities: ["forms.builder", "forms.analytics", "forms.logic"],
    minPlan: "core",
    minPlanLabel: "Core",
    category: "module",
    iconKey: "FileText",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 8,
    isActive: true,
  },
  {
    id: "addon_committees",
    name: "Committees & Governance",
    description: "Committee management with member assignments and meeting tracking",
    marketingDescription:
      "Committee management with member assignments, meeting tracking, and governance documentation.",
    price: "$99/mo",
    modules: ["committees"],
    capabilities: ["committees.management", "committees.meetings"],
    minPlan: "professional",
    minPlanLabel: "Growth",
    category: "module",
    iconKey: "Users",
    audienceType: "both",
    visibleOnPricing: true,
    visibleInAdmin: true,
    sortOrder: 9,
    isActive: true,
  },
  {
    id: "bundle_growth_pack",
    name: "Growth Pack",
    description: "Funnels + Forms + Communications Journeys bundled together",
    price: "$249/mo",
    modules: ["funnels", "forms"],
    capabilities: [
      "funnels.builder",
      "funnels.templates",
      "funnels.analytics",
      "forms.builder",
      "forms.analytics",
      "forms.logic",
      "communications.journeys",
    ],
    minPlan: "core",
    minPlanLabel: "Core",
    category: "bundle",
    iconKey: "Package",
    audienceType: "org",
    visibleOnPricing: false,
    visibleInAdmin: true,
    sortOrder: 20,
    isActive: true,
  },
];

/** Add-ons visible on the public pricing page */
export const PRICING_ADD_ONS = ADD_ONS.filter((a) => a.visibleOnPricing && a.isActive).sort(
  (a, b) => a.sortOrder - b.sortOrder,
);

/** Add-ons visible in admin packaging UI */
export const ADMIN_ADD_ONS = ADD_ONS.filter((a) => a.visibleInAdmin && a.isActive).sort(
  (a, b) => a.sortOrder - b.sortOrder,
);

// ─── Access State ────────────────────────────────────────────────────────────

export type AccessState =
  | "included" // Fully accessible via plan
  | "addon" // Available as a purchasable add-on
  | "trial" // Temporarily unlocked (promo/trial)
  | "bonus" // Bonus unlock (save-the-sale, promo)
  | "grandfathered" // Legacy access from prior plan
  | "locked" // Not available, upgrade required
  | "hidden" // Not visible at all
  | "coming_soon"; // Platform-level not yet available

export type GateBehavior = "show" | "tease" | "lock" | "hide";

/**
 * Determines UI behavior for a given access state.
 */
export function getGateBehavior(state: AccessState): GateBehavior {
  switch (state) {
    case "included":
    case "trial":
    case "bonus":
    case "grandfathered":
      return "show";
    case "addon":
      return "tease";
    case "locked":
    case "coming_soon":
      return "lock";
    case "hidden":
      return "hide";
  }
}

/**
 * Returns a human-readable badge label for access state (for UI badges).
 */
export function getAccessBadge(
  state: AccessState,
): { label: string; variant: "default" | "secondary" | "outline" | "destructive" } | null {
  switch (state) {
    case "trial":
      return { label: "Trial", variant: "secondary" };
    case "bonus":
      return { label: "Bonus", variant: "secondary" };
    case "grandfathered":
      return { label: "Legacy", variant: "outline" };
    case "addon":
      return { label: "Upgrade", variant: "default" };
    case "coming_soon":
      return { label: "Coming Soon", variant: "outline" };
    default:
      return null;
  }
}

/**
 * Find which plan includes a given module.
 */
export function getMinPlanForModule(moduleKey: string): PlanDefinition | undefined {
  return PLAN_LIST.find((p) => p.includedModules.includes(moduleKey));
}

/**
 * Find which plan includes a given capability.
 */
export function getMinPlanForCapability(capKey: string): PlanDefinition | undefined {
  return PLAN_LIST.find((p) => p.includedCapabilities.includes(capKey));
}

/**
 * Find add-ons that unlock a given module.
 */
export function getAddOnsForModule(moduleKey: string): AddOnDefinition[] {
  return ADD_ONS.filter((a) => a.modules.includes(moduleKey));
}

/**
 * Find add-ons that unlock a given capability.
 */
export function getAddOnsForCapability(capKey: string): AddOnDefinition[] {
  return ADD_ONS.filter((a) => a.capabilities.includes(capKey));
}
